import { dataPortalClient } from './data-portal.client';

export type DataPortalColumnType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'time'
  | 'datetime';
export type DataPortalColumnSemanticType = 'identifier';
export type DataPortalParameterType = 'text' | 'number' | 'boolean' | 'date';
export type DataPortalCell = string | number | boolean | null;
export type DataPortalRow = Record<string, DataPortalCell>;
export type DataPortalParameterValue = string | number | boolean;
export type DataPortalParameters = Record<string, DataPortalParameterValue>;

export interface DataPortalViewSummary {
  key: string;
  displayName: string;
  description: string;
  /** Legacy query number such as `012`; `null` for views without a legacy name. */
  legacyNumber: string | null;
}

/** Title shown in navigation and headings, e.g. `012: Kölner Aussteller`. */
export function viewTitle(view: Pick<DataPortalViewSummary, 'displayName' | 'legacyNumber'>): string {
  return view.legacyNumber ? `${view.legacyNumber}: ${view.displayName}` : view.displayName;
}

function isLegacyNumber(value: unknown): value is string | null {
  return value === null || (typeof value === 'string' && /^\d{3}[A-Z]?$/.test(value));
}

export interface DataPortalColumn {
  key: string;
  label: string;
  type: DataPortalColumnType;
  semanticType?: DataPortalColumnSemanticType;
}

export interface DataPortalParameterValidation {
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  integer?: boolean;
}

export interface DataPortalParameter {
  key: string;
  displayName: string;
  description: string;
  type: DataPortalParameterType;
  required: boolean;
  defaultValue: DataPortalCell;
  validation: DataPortalParameterValidation | null;
}

export interface DataPortalView extends DataPortalViewSummary {
  columns: DataPortalColumn[];
  parameters: DataPortalParameter[];
}

/** The complete result of one execution (ADR 0014: no server-side pagination). */
export interface DataPortalRowsResponse {
  columns: DataPortalColumn[];
  rows: DataPortalRow[];
}

export interface DataPortalRowsRequest {
  parameters?: DataPortalParameters;
  signal?: AbortSignal;
}

const columnTypes: readonly DataPortalColumnType[] = [
  'text',
  'number',
  'boolean',
  'date',
  'time',
  'datetime',
];
const parameterTypes: readonly DataPortalParameterType[] = [
  'text',
  'number',
  'boolean',
  'date',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function hasExactKeys(value: Record<string, unknown>, keys: string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => key in value);
}

function isViewSummary(value: unknown): value is DataPortalViewSummary {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['key', 'displayName', 'description', 'legacyNumber'])
  ) {
    return false;
  }
  return (
    isNonEmptyString(value.key) &&
    isNonEmptyString(value.displayName) &&
    typeof value.description === 'string' &&
    isLegacyNumber(value.legacyNumber)
  );
}

function isColumn(value: unknown): value is DataPortalColumn {
  if (
    !isRecord(value) ||
    !(
      hasExactKeys(value, ['key', 'label', 'type']) ||
      hasExactKeys(value, ['key', 'label', 'type', 'semanticType'])
    )
  ) {
    return false;
  }
  return (
    isNonEmptyString(value.key) &&
    isNonEmptyString(value.label) &&
    columnTypes.includes(value.type as DataPortalColumnType) &&
    (value.semanticType === undefined || value.semanticType === 'identifier')
  );
}

function isValidation(value: unknown): value is DataPortalParameterValidation | null {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  const allowed = [
    'pattern',
    'minLength',
    'maxLength',
    'minimum',
    'maximum',
    'integer',
  ];
  if (Object.keys(value).some((key) => !allowed.includes(key))) return false;
  return Object.entries(value).every(([key, entry]) => {
    if (key === 'pattern') {
      if (typeof entry !== 'string') return false;
      try {
        new RegExp(entry);
        return true;
      } catch {
        return false;
      }
    }
    if (key === 'integer') return typeof entry === 'boolean';
    return typeof entry === 'number' && Number.isFinite(entry);
  });
}

function isParameterDefault(value: unknown, type: DataPortalParameterType): boolean {
  if (value === null) return true;
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'boolean') return typeof value === 'boolean';
  return typeof value === 'string';
}

function isParameter(value: unknown): value is DataPortalParameter {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      'key',
      'displayName',
      'description',
      'type',
      'required',
      'defaultValue',
      'validation',
    ]) ||
    !parameterTypes.includes(value.type as DataPortalParameterType)
  ) {
    return false;
  }
  return (
    isNonEmptyString(value.key) &&
    isNonEmptyString(value.displayName) &&
    typeof value.description === 'string' &&
    typeof value.required === 'boolean' &&
    isParameterDefault(value.defaultValue, value.type as DataPortalParameterType) &&
    isValidation(value.validation)
  );
}

function hasUniqueKeys(items: Array<{ key: string }>): boolean {
  return new Set(items.map((item) => item.key)).size === items.length;
}

function parseViews(value: unknown): DataPortalViewSummary[] {
  if (!isRecord(value) || !hasExactKeys(value, ['views']) || !Array.isArray(value.views)) {
    throw new TypeError('Invalid data portal views response');
  }
  if (!value.views.every(isViewSummary) || !hasUniqueKeys(value.views)) {
    throw new TypeError('Invalid data portal views response');
  }
  // Views without a legacy number keep their catalog order and come first.
  return value.views
    .map((view, index) => ({ view, index }))
    .sort(
      (a, b) =>
        (a.view.legacyNumber ?? '').localeCompare(b.view.legacyNumber ?? '') ||
        a.index - b.index,
    )
    .map(({ view }) => view);
}

function parseView(value: unknown): DataPortalView {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      'key',
      'displayName',
      'description',
      'legacyNumber',
      'columns',
      'parameters',
    ]) ||
    !isNonEmptyString(value.key) ||
    !isNonEmptyString(value.displayName) ||
    typeof value.description !== 'string' ||
    !isLegacyNumber(value.legacyNumber) ||
    !Array.isArray(value.columns) ||
    !value.columns.length ||
    !value.columns.every(isColumn) ||
    !hasUniqueKeys(value.columns) ||
    !Array.isArray(value.parameters) ||
    !value.parameters.every(isParameter) ||
    !hasUniqueKeys(value.parameters) ||
    value.parameters.some((parameter) => reservedQueryKeys.has(parameter.key))
  ) {
    throw new TypeError('Invalid data portal view response');
  }
  return value as unknown as DataPortalView;
}

function isDateValue(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith('0000-')) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value;
}

function isTimeValue(value: string): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,7})?$/.test(value);
}

function isDateTimeValue(value: string): boolean {
  if (
    !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,7})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(
      value,
    )
  ) {
    return false;
  }
  return isDateValue(value.slice(0, 10)) && !Number.isNaN(Date.parse(value));
}

function isCell(value: unknown, type: DataPortalColumnType): value is DataPortalCell {
  if (value === null) return true;
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'boolean') return typeof value === 'boolean';
  if (typeof value !== 'string') return false;
  if (type === 'date') return isDateValue(value);
  if (type === 'time') return isTimeValue(value);
  if (type === 'datetime') return isDateTimeValue(value);
  return true;
}

// Controls of the retired paged runtime; the backend rejects them (ADR 0014).
const reservedQueryKeys = new Set(['limit', 'cursor', 'sort']);

function validateParameters(
  parameters: DataPortalParameters,
  metadata: DataPortalView,
): void {
  if (!isRecord(parameters)) throw new TypeError('Invalid data portal parameters');
  const definitions = new Map(
    metadata.parameters.map((parameter) => [parameter.key, parameter]),
  );
  for (const key of Object.keys(parameters)) {
    if (reservedQueryKeys.has(key) || !definitions.has(key)) {
      throw new TypeError('Invalid data portal parameter');
    }
  }
  for (const definition of metadata.parameters) {
    const present = Object.prototype.hasOwnProperty.call(parameters, definition.key);
    if (!present) {
      if (definition.required) throw new TypeError('Missing data portal parameter');
      continue;
    }
    const value = parameters[definition.key];
    const validation = definition.validation;
    if (definition.type === 'number') {
      if (
        typeof value !== 'number' ||
        !Number.isFinite(value) ||
        (validation?.integer === true && !Number.isSafeInteger(value)) ||
        (validation?.minimum !== undefined && value < validation.minimum) ||
        (validation?.maximum !== undefined && value > validation.maximum)
      ) {
        throw new TypeError('Invalid data portal parameter');
      }
      continue;
    }
    if (definition.type === 'boolean') {
      if (typeof value !== 'boolean') throw new TypeError('Invalid data portal parameter');
      continue;
    }
    if (typeof value !== 'string' || !value) {
      throw new TypeError('Invalid data portal parameter');
    }
    if (definition.type === 'date' && !isDateValue(value)) {
      throw new TypeError('Invalid data portal parameter');
    }
    if (
      (validation?.minLength !== undefined && value.length < validation.minLength) ||
      (validation?.maxLength !== undefined && value.length > validation.maxLength) ||
      (validation?.pattern !== undefined && !new RegExp(validation.pattern).test(value))
    ) {
      throw new TypeError('Invalid data portal parameter');
    }
  }
}

function parseRows(value: unknown, metadata: DataPortalView): DataPortalRowsResponse {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ['columns', 'rows']) ||
    !Array.isArray(value.columns) ||
    value.columns.length !== metadata.columns.length ||
    !value.columns.every(
      (column, index) =>
        isColumn(column) &&
        column.key === metadata.columns[index].key &&
        column.label === metadata.columns[index].label &&
        column.type === metadata.columns[index].type &&
        column.semanticType === metadata.columns[index].semanticType,
    ) ||
    !Array.isArray(value.rows)
  ) {
    throw new TypeError('Invalid data portal rows response');
  }

  const keys = metadata.columns.map((column) => column.key);
  const validRows = value.rows.every((candidate) => {
    if (!isRecord(candidate) || !hasExactKeys(candidate, keys)) return false;
    return metadata.columns.every((column) => isCell(candidate[column.key], column.type));
  });
  if (!validRows) throw new TypeError('Invalid data portal rows response');
  return value as unknown as DataPortalRowsResponse;
}

export const dataPortalViewsApi = {
  async listViews(signal?: AbortSignal): Promise<DataPortalViewSummary[]> {
    const response = await dataPortalClient.get<unknown>('/views', { signal });
    return parseViews(response.data);
  },

  async getView(viewKey: string, signal?: AbortSignal): Promise<DataPortalView> {
    const response = await dataPortalClient.get<unknown>(
      `/views/${encodeURIComponent(viewKey)}`,
      { signal },
    );
    const view = parseView(response.data);
    if (view.key !== viewKey) throw new TypeError('Mismatched data portal view response');
    return view;
  },

  async getRows(
    viewKey: string,
    metadata: DataPortalView,
    { parameters = {}, signal }: DataPortalRowsRequest = {},
  ): Promise<DataPortalRowsResponse> {
    if (metadata.key !== viewKey) {
      throw new TypeError('Invalid data portal rows request');
    }
    validateParameters(parameters, metadata);
    const response = await dataPortalClient.get<unknown>(
      `/views/${encodeURIComponent(viewKey)}/rows`,
      { params: { ...parameters }, signal },
    );
    return parseRows(response.data, metadata);
  },
};
